import React from "react";
import { Quote, MessageSquare } from "lucide-react";
import { Card } from "@/components/ui";
import { Review } from "@/services/bedrock";
import { format } from "date-fns";
import {
  getInitials,
  SOURCE_SYSTEM_CONFIG,
  getSourceIcon,
  getReviewSentiment,
  getSentimentStyles,
  getInternalBadgeName,
} from "./utils";
import { ReviewReplyItem } from "./ReviewReplyItem";
import { ReviewRating } from "./ReviewRating";
import { useIsMobile } from "@/utils";

interface ReviewHeroCardProps {
  review: Review;
  hideReplies?: boolean;
}

export function ReviewHeroCard({ review, hideReplies = false }: ReviewHeroCardProps) {
  const isMobile = useIsMobile();
  const sentiment = getReviewSentiment(review);
  const theme = getSentimentStyles(sentiment);

  const isInternal = SOURCE_SYSTEM_CONFIG[review.sourceSystem]?.isInternal;

  return (
    <Card className="p-4 md:p-8 space-y-4 md:space-y-6 border border-slate-200 rounded-2xl bg-white shadow-sm shrink-0">
      {/* Header: Identity and Rating */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0">
          {/* Initials Avatar with Source Badge */}
          <div className="relative shrink-0">
            <div
              className={`size-10 md:size-14 rounded-full ${theme.avatarGradient} text-white font-bold text-lg md:text-xl flex items-center justify-center shadow-sm uppercase`}
            >
              {getInitials(review.displayName)}
            </div>
            <div className="absolute -bottom-1 -right-1 md:-bottom-1.5 md:-right-1.5 bg-white p-0.5 rounded-full shadow-sm ring-1 ring-slate-100 flex items-center justify-center">
              {getSourceIcon(review.sourceSystem, "xs")}
            </div>
          </div>

          {/* Name and Meta */}
          <div className="min-w-0 flex flex-col pt-0.5">
            <h2 className="text-lg md:text-xl font-extrabold text-slate-800 truncate tracking-tight mb-0.5 md:mb-1.5">
              {review.displayName || "Anonymous"}
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold text-slate-400">
                {review.effectiveDate || review.publishedAt
                  ? format(new Date(review.effectiveDate || review.publishedAt || ""), "MMM d, yyyy")
                  : "Unknown Date"}
              </span>
              {isInternal && (
                <>
                  <span className="text-xs font-bold text-slate-400">•</span>
                  <span className="flex flex-row gap-1 text-xs font-bold text-slate-400">
                    {getInternalBadgeName(review)}
                  </span>
                </>
              )}
              {review.form?.displayName && (
                <>
                  <span className="text-xs font-bold text-slate-400">•</span>
                  <span className="text-xs font-bold text-slate-400">{review.form.displayName}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Rating Top Right */}
        <div className="shrink-0">
          <ReviewRating review={review} fallback="No Rating" />
        </div>
      </div>

      {/* Content Text */}
      <div className="relative pl-1">
        {review.content ? (
          <>
            <Quote className="absolute -top-3 -left-3 size-10 text-slate-100 rotate-180" />
            <p className="text-base md:text-lg text-slate-800 leading-relaxed font-medium relative">{review.content}</p>
          </>
        ) : (
          <p className="text-sm italic font-medium text-slate-400">This reviewer did not write a comment.</p>
        )}
      </div>

      {/* Threaded Replies Section */}
      {review.reviewReplies && review.reviewReplies.length > 0 && !hideReplies && (
        <div className="flex flex-col gap-4 border-t border-slate-100 pt-6">
          <h4 className="text-xs font-extrabold text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
            <MessageSquare className="size-4 text-slate-400" />
            Response Threads ({review.reviewReplies.length})
          </h4>

          <div className="flex flex-col gap-4">
            {review.reviewReplies.map((reply, idx) => (
              <ReviewReplyItem key={idx} reply={reply} />
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
